import { useCallback, useEffect, useMemo, useRef, type Dispatch, type SetStateAction } from 'react';

import { useSearchQueryValue } from './useDebouncedSearch';

export type FilterQueryParams = {
  page: number;
  filters: Record<string, string>;
};

// eslint-disable-next-line no-unused-vars
type FilterQueryFn = (params: FilterQueryParams) => void;

type UseFilterQueryEffectOptions = {
  filters: Record<string, string>;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  query: FilterQueryFn;
  debounceMs?: number;
};

/** Trimmed, empty keys dropped, so "  " and "" mean "no filter". */
export const cleanFilters = (filters: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(filters)
      .map(([key, value]) => [key, (value ?? '').trim()])
      .filter(([, value]) => value !== ''),
  );

/**
 * The multi-filter counterpart of useSearchQueryEffect: debounces the whole
 * filter set as one value, restarts at page 1 whenever any filter changes, and
 * keeps page-turn fetches separate so a change never fires two requests.
 *
 * Returns the debounced filters (`applied`) for callers that refetch outside
 * the hook, and `refresh()`, which re-runs the current page with them — the
 * Refresh button on every list.
 */
export function useFilterQueryEffect({
  filters,
  currentPage,
  setCurrentPage,
  query,
  debounceMs,
}: UseFilterQueryEffectOptions): { applied: Record<string, string>; refresh: () => void } {
  // Pickers change in one step and free text keystroke by keystroke; one
  // debounce over the serialised set treats them the same and stays simple.
  const serialised = JSON.stringify(cleanFilters(filters));
  const debounced = useSearchQueryValue(serialised, debounceMs);
  const applied = useMemo<Record<string, string>>(() => (debounced ? JSON.parse(debounced) : {}), [debounced]);
  const skipPaginationRef = useRef(false);

  useEffect(() => {
    skipPaginationRef.current = true;
    query({ page: 1, filters: applied });
    setCurrentPage(1);
  }, [applied, query, setCurrentPage]);

  useEffect(() => {
    if (skipPaginationRef.current) {
      skipPaginationRef.current = false;
      return;
    }
    query({ page: currentPage, filters: applied });
  }, [currentPage, applied, query]);

  const refresh = useCallback(() => query({ page: currentPage, filters: applied }), [applied, currentPage, query]);

  return { applied, refresh };
}
